import { DateTime } from '@ksyos/date-time';
import { FormSection } from '@ksyos/design-system';
import { isBoolean } from 'lodash';
import type { IAssets } from '@common/validation/assets';
import { OsasPapType } from '@common/enums/osasPapType';
import { behandelingLabels } from '@common/interfaces/somnology/osase/treatmentsFields';
import { osaseFilePlacement } from '@common/validation/customControls/somnology';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { RequestAssetThumbnails } from '@frontend/components/pages/episodes/common';
import { translateKey } from '@frontend/translate';
import type { IOsaseV2RequestCustomControlData } from './OsaseV2RequestAddEdit';
import { getFilesByPlacement } from './OsaseV2RequestAddEdit';

export type IOsaseV2RequestReadCustomControlFields = Omit<
    IOsaseV2RequestCustomControlData,
    'beleid' | 'anamnese' | 'behandelend_arts_id'
> & {
    requestId: string;
};

type IOsaseFilesByPlacement = {
    [key in osaseFilePlacement]: IAssets;
};

export function OsaseV2RequestReadCustomControlFields(props: IOsaseV2RequestReadCustomControlFields) {
    const osaseFilesByPlacement: IOsaseFilesByPlacement = getOsaseFiles();
    const shouldDisplayAnamnese =
        !props.behandeling.spt &&
        !props.behandeling.pap &&
        props.behandeling.leefstijl_verandering === !props.behandeling.mra;

    return (
        <>
            {props.behandelend_arts_name && (
                <FormSection isBorderless>
                    <FormParagraphFormGroup label="Behandelend arts">
                        {props.behandelend_arts_name}
                    </FormParagraphFormGroup>
                </FormSection>
            )}
            {props.behandeling.pap && (
                <>
                    {isAnyInterfacePropertyFilled(
                        props.pap.periode_rapport_start,
                        props.pap.periode_rapport_end,
                        osaseFilesByPlacement.papReport.length > 0
                    ) && (
                        <FormSection
                            isBorderless
                            title="PAP rapport"
                        >
                            {(props.pap.periode_rapport_start || props.pap.periode_rapport_end) && (
                                <FormParagraphFormGroup label="Periode rapport">
                                    {getPeriodRapport(props.pap.periode_rapport_start, props.pap.periode_rapport_end)}
                                </FormParagraphFormGroup>
                            )}

                            {osaseFilesByPlacement.papReport.length > 0 && (
                                <FormParagraphFormGroup label="Rapport">
                                    <RequestAssetThumbnails
                                        assets={osaseFilesByPlacement.papReport}
                                        requestId={props.requestId}
                                    />
                                </FormParagraphFormGroup>
                            )}
                        </FormSection>
                    )}
                    {isAnyInterfacePropertyFilled(
                        props.pap.druk.value,
                        props.pap.minimale_druk.value,
                        props.pap.maximale_druk.value,
                        props.pap.ninetyfive_druk.value,
                        props.pap.druk_ipap.value,
                        props.pap.druk_epap.value
                    ) && (
                        <FormSection
                            isBorderless
                            title="Apparaat en instellingen"
                        >
                            <>
                                {props.pap.type === OsasPapType.cpap && props.pap.druk.value && (
                                    <FormParagraphFormGroup label="Druk">
                                        {`${props.pap.druk.value} ${props.pap.druk.unit}`}
                                    </FormParagraphFormGroup>
                                )}

                                {props.pap.type === OsasPapType.apap && (
                                    <>
                                        {props.pap.minimale_druk.value && (
                                            <FormParagraphFormGroup label="Minimale druk">
                                                {`${props.pap.minimale_druk.value} ${props.pap.minimale_druk.unit}`}
                                            </FormParagraphFormGroup>
                                        )}
                                        {props.pap.maximale_druk.value && (
                                            <FormParagraphFormGroup label="Maximale druk">
                                                {`${props.pap.maximale_druk.value} ${props.pap.maximale_druk.unit}`}
                                            </FormParagraphFormGroup>
                                        )}
                                        {props.pap.ninetyfive_druk.value && (
                                            <FormParagraphFormGroup label="95% druk">
                                                {`${props.pap.ninetyfive_druk.value} ${props.pap.ninetyfive_druk.unit}`}
                                            </FormParagraphFormGroup>
                                        )}
                                    </>
                                )}

                                {props.pap.type === OsasPapType.biPap && (
                                    <>
                                        {props.pap.druk_ipap.value && (
                                            <FormParagraphFormGroup label="Druk IPAP (Inhale)">
                                                {`${props.pap.druk_ipap.value} ${props.pap.druk_ipap.unit}`}
                                            </FormParagraphFormGroup>
                                        )}
                                        {props.pap.druk_epap.value && (
                                            <FormParagraphFormGroup label="Druk EPAP (Exhale)">
                                                {`${props.pap.druk_epap.value} ${props.pap.druk_epap.unit}`}
                                            </FormParagraphFormGroup>
                                        )}
                                    </>
                                )}
                            </>
                        </FormSection>
                    )}
                    {isAnyInterfacePropertyFilled(props.pap.nachten_met_ondersteuning, props.pap.gemiddeld_gebruik) && (
                        <FormSection
                            isBorderless
                            title="Gebruik PAP"
                        >
                            {props.pap.nachten_met_ondersteuning && (
                                <FormParagraphFormGroup label="Nachten met ondersteuning">
                                    {props.pap.nachten_met_ondersteuning}
                                </FormParagraphFormGroup>
                            )}
                            {props.pap.gemiddeld_gebruik && (
                                <FormParagraphFormGroup label="Gemiddeld gebruik">
                                    {props.pap.gemiddeld_gebruik.concat(' uur / nacht')}
                                </FormParagraphFormGroup>
                            )}
                        </FormSection>
                    )}
                    {isAnyInterfacePropertyFilled(
                        props.pap.ahi,
                        props.pap.obstructief_apneu,
                        props.pap.centraal_apneu,
                        props.pap.cheyne_stokes,
                        props.pap.spo2,
                        props.pap.extra_waarden_uit_rapport
                    ) && (
                        <FormSection
                            isBorderless
                            title="Waarden PAP"
                        >
                            {props.pap.ahi && (
                                <FormParagraphFormGroup label="pAHI">
                                    {props.pap.ahi.concat(' / uur')}
                                </FormParagraphFormGroup>
                            )}
                            {props.pap.obstructief_apneu && (
                                <FormParagraphFormGroup label="Obstructief apneu">
                                    {props.pap.obstructief_apneu.concat(' / uur')}
                                </FormParagraphFormGroup>
                            )}
                            {props.pap.centraal_apneu && (
                                <FormParagraphFormGroup label="Centraal apneu">
                                    {props.pap.centraal_apneu.concat(' / uur')}
                                </FormParagraphFormGroup>
                            )}
                            {props.pap.cheyne_stokes && (
                                <FormParagraphFormGroup label="Cheyne-stokes">
                                    {props.pap.cheyne_stokes}
                                </FormParagraphFormGroup>
                            )}
                            {props.pap.spo2 && (
                                <FormParagraphFormGroup label="SPO2">{props.pap.spo2}</FormParagraphFormGroup>
                            )}

                            {props.pap.extra_waarden_uit_rapport && (
                                <FormParagraphFormGroup label="Extra waarden uit rapport">
                                    {props.pap.extra_waarden_uit_rapport}
                                </FormParagraphFormGroup>
                            )}
                        </FormSection>
                    )}
                </>
            )}
            {props.behandeling.spt && (
                <>
                    {isAnyInterfacePropertyFilled(
                        props.spt.periode_rapport_start,
                        props.spt.periode_rapport_end,
                        osaseFilesByPlacement.sptReport.length > 0
                    ) && (
                        <FormSection
                            isBorderless
                            title="SPT rapport"
                        >
                            {(props.spt.periode_rapport_start || props.spt.periode_rapport_end) && (
                                <FormParagraphFormGroup label="Periode rapport">
                                    {getPeriodRapport(props.spt.periode_rapport_start, props.spt.periode_rapport_end)}
                                </FormParagraphFormGroup>
                            )}
                            {osaseFilesByPlacement.sptReport.length > 0 && (
                                <FormParagraphFormGroup label="Rapport">
                                    <RequestAssetThumbnails
                                        assets={osaseFilesByPlacement.sptReport}
                                        requestId={props.requestId}
                                    />
                                </FormParagraphFormGroup>
                            )}
                        </FormSection>
                    )}
                    {isAnyInterfacePropertyFilled(props.spt.nachten_met_ondersteuning, props.spt.gemiddeld_gebruik) && (
                        <FormSection
                            isBorderless
                            title="Gebruik SPT"
                        >
                            {props.spt.nachten_met_ondersteuning && (
                                <FormParagraphFormGroup label="Nachten met ondersteuning">
                                    {props.spt.nachten_met_ondersteuning}
                                </FormParagraphFormGroup>
                            )}
                            {props.spt.gemiddeld_gebruik && (
                                <FormParagraphFormGroup label="Gemiddeld gebruik">
                                    {props.spt.gemiddeld_gebruik.concat(' uur / nacht')}
                                </FormParagraphFormGroup>
                            )}
                        </FormSection>
                    )}

                    {isAnyInterfacePropertyFilled(
                        props.spt.rugligging_van,
                        props.spt.rugligging_tot,
                        props.spt.toelichting
                    ) && (
                        <FormSection
                            isBorderless
                            title="Waarden SPT"
                        >
                            {(props.spt.rugligging_van || props.spt.rugligging_tot) && (
                                <FormParagraphFormGroup label="Rugligging">
                                    {`van  ${props.spt.rugligging_van ?? ''}%  naar  ${
                                        props.spt.rugligging_tot ?? ''
                                    }%`}
                                </FormParagraphFormGroup>
                            )}
                            {props.spt.toelichting && (
                                <FormParagraphFormGroup label="Toelichting">
                                    {props.spt.toelichting}
                                </FormParagraphFormGroup>
                            )}
                        </FormSection>
                    )}
                </>
            )}
            {props.behandeling.mra && displayMraFields()}

            {props.behandeling.leefstijl_verandering && displayLeefstijlFields()}

            {isAnyInterfacePropertyFilled(
                props.behandeling.spt,
                props.behandeling.pap,
                props.behandeling.leefstijl_verandering && props.behandeling.mra
            ) &&
                isAnyInterfacePropertyFilled(
                    isBoolean(props.contact_met_patient_gehad),
                    props.klachtenlast,
                    props.proefperiode,
                    props.toelichting
                ) && (
                    <FormSection
                        isBorderless
                        title="Anamnese"
                    >
                        {displayHadContactWithPatient()}
                        {displayHadContactWithPatientExtraFields()}
                    </FormSection>
                )}

            {isBoolean(props.huisarts_op_de_hoogte_brengen) && (
                <FormSection
                    isBorderless
                    title="Informeren"
                >
                    <FormParagraphFormGroup label="Huisarts op de hoogte brengen">
                        {props.huisarts_op_de_hoogte_brengen ? translateKey('common.yes') : translateKey('common.no')}
                    </FormParagraphFormGroup>
                </FormSection>
            )}
        </>
    );

    function displayHadContactWithPatient() {
        return (
            isBoolean(props.contact_met_patient_gehad) && (
                <FormParagraphFormGroup label="Contact met patiënt gehad?">
                    {props.contact_met_patient_gehad ? translateKey('common.yes') : translateKey('common.no')}
                </FormParagraphFormGroup>
            )
        );
    }

    function displayHadContactWithPatientExtraFields() {
        return (
            (props.behandeling.spt || props.behandeling.pap || props.behandeling.mra) &&
            props.contact_met_patient_gehad && (
                <>
                    {props.klachtenlast && (
                        <FormParagraphFormGroup label="Klachtenlast">{props.klachtenlast}</FormParagraphFormGroup>
                    )}
                    {props.proefperiode && (
                        <FormParagraphFormGroup label="Proefperiode">{props.proefperiode}</FormParagraphFormGroup>
                    )}
                    {props.toelichting && (
                        <FormParagraphFormGroup label="Toelichting">{props.toelichting}</FormParagraphFormGroup>
                    )}
                </>
            )
        );
    }

    function displayMraFields() {
        if (
            isAnyInterfacePropertyFilled(
                isBoolean(props.contact_met_patient_gehad) && shouldDisplayAnamnese,
                props.mra_volgt_patient_het_beleid,
                props.klachtenlast,
                props.proefperiode,
                props.toelichting,
                osaseFilesByPlacement.mraAttachment.length > 0
            )
        ) {
            return (
                <FormSection
                    isBorderless
                    title={shouldDisplayAnamnese ? 'Anamnese' : behandelingLabels.mra}
                >
                    {shouldDisplayAnamnese && displayHadContactWithPatient()}

                    {props.mra_volgt_patient_het_beleid && (
                        <FormParagraphFormGroup label="Volgt patiënt het beleid?">
                            {props.mra_volgt_patient_het_beleid}
                        </FormParagraphFormGroup>
                    )}

                    {shouldDisplayAnamnese && displayHadContactWithPatientExtraFields()}

                    {osaseFilesByPlacement.mraAttachment.length > 0 && (
                        <FormParagraphFormGroup label="Bijlage">
                            <RequestAssetThumbnails
                                assets={osaseFilesByPlacement.mraAttachment}
                                requestId={props.requestId}
                            />
                        </FormParagraphFormGroup>
                    )}
                </FormSection>
            );
        }
    }

    function displayLeefstijlFields() {
        if (
            isAnyInterfacePropertyFilled(
                props.leefstijl_volgt_patient_het_beleid,
                osaseFilesByPlacement.leefstijlAttachment.length > 0
            )
        ) {
            return (
                <FormSection
                    isBorderless
                    title={shouldDisplayAnamnese ? 'Anamnese' : behandelingLabels.leefstijl_verandering}
                >
                    {props.leefstijl_volgt_patient_het_beleid && (
                        <FormParagraphFormGroup label="Volgt patiënt het beleid?">
                            {props.leefstijl_volgt_patient_het_beleid}
                        </FormParagraphFormGroup>
                    )}
                    {osaseFilesByPlacement.leefstijlAttachment.length > 0 && (
                        <FormParagraphFormGroup label="Bijlage">
                            <RequestAssetThumbnails
                                assets={osaseFilesByPlacement.leefstijlAttachment}
                                requestId={props.requestId}
                            />
                        </FormParagraphFormGroup>
                    )}
                </FormSection>
            );
        }
    }

    function getPeriodRapport(startDate: string | null, endDate: string | null) {
        return `${startDate ? DateTime.from(startDate).format('DD-MM-YYYY') : ''}   ${translateKey('common.until')}   ${
            endDate ? DateTime.from(endDate).format('DD-MM-YYYY') : ''
        }`;
    }

    function getOsaseFiles() {
        const osaseFiles: IOsaseFilesByPlacement = {} as IOsaseFilesByPlacement;

        for (const key of Object.keys(osaseFilePlacement) as Array<keyof IOsaseFilesByPlacement>) {
            osaseFiles[key] = getFilesByPlacement(props.files, key);
        }

        return osaseFiles;
    }

    function isAnyInterfacePropertyFilled(...values: (string | boolean | null)[]) {
        return values.some((value) => !!value);
    }
}
